import { ThemeToggle } from './ThemeToggle.jsx';
import { CoralMark } from './CoralMark.jsx';

const ITEMS = [
  {
    k: 'home',
    label: 'Home',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    k: 'log',
    label: 'Log',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 5v14M5 12h14" />
      </svg>
    ),
  },
  {
    k: 'tasks',
    label: 'Tasks',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="9 11 12 14 22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    ),
  },
  {
    // The Tank tab (owner, 2026-10-21): supplies, dosing, equipment,
    // livestock, history and settings, one tap each. A new feature gets a
    // tile there, never a new tab (CLAUDE.md, "Don't crowd the app").
    k: 'tank',
    label: 'Tank',
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="5" width="18" height="15" rx="2" />
        <path d="M3 10c2 0 2-1.5 4.5-1.5S9.5 10 12 10s2.5-1.5 4.5-1.5S19 10 21 10" />
        <path d="M8 20v-4M12 20v-2.5M16 20v-5" />
      </svg>
    ),
  },
];

// Bottom nav — shown on mobile/tablet. Hidden on desktop by CSS.
const NEAR_ICON = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

export function BottomNav({ tab, setTab, onOpenNear, nearActive = false }) {
  const itemStyle = (active) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '8px 0',
    borderRadius: 999,
    background: active ? 'var(--cyan)' : 'transparent',
    color: active ? 'var(--cyan-text)' : 'var(--text-muted)',
  });
  const labelStyle = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.2em' };
  return (
    <nav className="bottom-nav">
      <div
        style={{
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          padding: 4,
          background: 'var(--surface)',
          border: '1px solid var(--surface-border)',
          backdropFilter: 'blur(12px)',
        }}
      >
        {ITEMS.map((it) => (
          <button
            key={it.k}
            onClick={() => setTab(it.k)}
            style={itemStyle(!nearActive && tab === it.k)}
          >
            {it.icon}
            <span className="font-heading" style={labelStyle}>
              {it.label}
            </span>
          </button>
        ))}
        {onOpenNear && (
          <button
            onClick={onOpenNear}
            style={itemStyle(nearActive)}
            aria-label="Near you"
            aria-current={nearActive ? 'page' : undefined}
          >
            {NEAR_ICON}
            <span className="font-heading" style={labelStyle}>
              Near
            </span>
          </button>
        )}
      </div>
    </nav>
  );
}

// Side nav — desktop only (≥ 1024px). Shown by CSS.
export function SideNav({
  tab,
  setTab,
  user,
  onSignOut,
  onOpenAccount,
  onOpenNear,
  nearActive = false,
  accountActive = false,
}) {
  return (
    <aside className="side-nav">
      <div
        className="font-heading"
        style={{ fontSize: 24, letterSpacing: '0.02em', marginBottom: 28 }}
      >
        <span style={{ color: 'var(--text)' }}>Tank</span>
        <span style={{ color: 'var(--cyan)' }}>Sensei</span>
        <CoralMark size={18} style={{ marginLeft: 6 }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {ITEMS.map((it) => (
          <button
            key={it.k}
            onClick={() => setTab(it.k)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              borderRadius: 10,
              textAlign: 'left',
              background: !nearActive && tab === it.k ? 'var(--accent-bg)' : 'transparent',
              color: !nearActive && tab === it.k ? 'var(--cyan)' : 'var(--text-muted)',
              border: `1px solid ${!nearActive && tab === it.k ? 'var(--accent-border)' : 'transparent'}`,
            }}
          >
            {it.icon}
            <span
              className="font-heading"
              style={{
                fontSize: 13,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >
              {it.label}
            </span>
          </button>
        ))}
        {onOpenNear && (
          <button
            onClick={onOpenNear}
            aria-label="Near you"
            aria-current={nearActive ? 'page' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              borderRadius: 10,
              textAlign: 'left',
              background: nearActive ? 'var(--accent-bg)' : 'transparent',
              color: nearActive ? 'var(--cyan)' : 'var(--text-muted)',
              border: `1px solid ${nearActive ? 'var(--accent-border)' : 'transparent'}`,
            }}
          >
            {NEAR_ICON}
            <span
              className="font-heading"
              style={{
                fontSize: 13,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >
              Near You
            </span>
          </button>
        )}
      </div>
      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {onOpenAccount && (
          <button
            onClick={onOpenAccount}
            aria-label="Account settings"
            aria-current={accountActive ? 'page' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              borderRadius: 10,
              textAlign: 'left',
              background: accountActive ? 'var(--accent-bg)' : 'transparent',
              color: accountActive ? 'var(--cyan)' : 'var(--text-muted)',
              border: `1px solid ${accountActive ? 'var(--accent-border)' : 'var(--surface-border)'}`,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                className="font-heading"
                style={{
                  display: 'block',
                  fontSize: 13,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                Account
              </span>
              {user?.email && (
                <span
                  className="font-mono"
                  style={{
                    display: 'block',
                    fontSize: 12,
                    color: 'var(--text-dim)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.email}
                </span>
              )}
            </span>
          </button>
        )}
        <ThemeToggle />
        <button
          onClick={onSignOut}
          className="font-heading"
          style={{
            padding: '8px 14px',
            borderRadius: 10,
            color: 'var(--text-muted)',
            border: '1px solid var(--surface-border)',
            fontSize: 12,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            textAlign: 'left',
          }}
        >
          Sign out
        </button>
      </div>
    </aside>
  );
}
